import { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Trophy, Star, Heart, Calendar, TrendingUp, Sparkles, ChevronRight } from 'lucide-react';
import { motion } from '../lib/motion-utils';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';
import { projectId, publicAnonKey } from '/utils/supabase/info';

interface Milestone {
  id: string;
  type: 'streak' | 'checkins' | 'tension_reduction' | 'stable_period' | 'action_cards' | 'first_week';
  title: string;
  description: string;
  icon: typeof Trophy;
  achieved: boolean;
  achievedDate?: string;
  value?: number;
}

export function MilestoneCelebrations() {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [newMilestone, setNewMilestone] = useState<Milestone | null>(null);
  const { accessToken } = useAuth();

  useEffect(() => {
    void checkMilestones();
  }, [accessToken]);

  const checkMilestones = async () => {
    const checkins = parseArrayStorage(localStorage.getItem('tension_checkins'));
    const actionCards = JSON.parse(localStorage.getItem('tension_action_cards_completed') || '[]');
    const achievedMilestones = parseArrayStorage(localStorage.getItem('tension_milestones'));
    const canonicalCheckInCount = await resolveCanonicalCheckInCount(checkins, accessToken);

    const potentialMilestones: Milestone[] = [
      {
        id: 'first_checkin',
        type: 'checkins',
        title: 'First Check-In',
        description: 'Started your tension tracking journey',
        icon: Sparkles,
        achieved: checkins.length >= 1,
        value: 1,
      },
      {
        id: 'week_1',
        type: 'first_week',
        title: '7-Day Streak',
        description: 'Checked in for 7 consecutive days',
        icon: Star,
        achieved: getConsecutiveDays(checkins) >= 7,
        value: 7,
      },
      {
        id: 'week_2',
        type: 'streak',
        title: '14-Day Streak',
        description: 'Two weeks of consistent tracking',
        icon: Trophy,
        achieved: getConsecutiveDays(checkins) >= 14,
        value: 14,
      },
      {
        id: 'month_1',
        type: 'streak',
        title: '30-Day Streak',
        description: 'A full month of dedication',
        icon: Trophy,
        achieved: getConsecutiveDays(checkins) >= 30,
        value: 30,
      },
      {
        id: 'checkins_50',
        type: 'checkins',
        title: '50 Check-Ins',
        description: 'Completed 50 daily check-ins',
        icon: Star,
        achieved: canonicalCheckInCount >= 50,
        value: 50,
      },
      {
        id: 'checkins_100',
        type: 'checkins',
        title: '100 Check-Ins',
        description: 'Century of self-awareness',
        icon: Trophy,
        achieved: canonicalCheckInCount >= 100,
        value: 100,
      },
      {
        id: 'tension_drop',
        type: 'tension_reduction',
        title: 'Tension Tamer',
        description: 'Reduced RTI by 20+ points in a week',
        icon: TrendingUp,
        achieved: hasSignificantTensionDrop(checkins),
      },
      {
        id: 'stable_week',
        type: 'stable_period',
        title: 'Week of Stability',
        description: '7 days in Stable zone (RTI 0-35)',
        icon: Heart,
        achieved: hasStableWeek(checkins),
      },
      {
        id: 'actions_10',
        type: 'action_cards',
        title: 'Action Taker',
        description: 'Completed 10 action cards',
        icon: ChevronRight,
        achieved: actionCards.length >= 10,
        value: 10,
      },
      {
        id: 'actions_50',
        type: 'action_cards',
        title: 'Action Master',
        description: 'Completed 50 action cards',
        icon: Trophy,
        achieved: actionCards.length >= 50,
        value: 50,
      },
    ];

    // Remove incorrectly granted check-in milestones if canonical count is lower.
    const correctedAchievedMilestones = achievedMilestones.filter((m: any) => {
      if (m?.id === 'checkins_50' && canonicalCheckInCount < 50) return false;
      if (m?.id === 'checkins_100' && canonicalCheckInCount < 100) return false;
      return true;
    });
    if (correctedAchievedMilestones.length !== achievedMilestones.length) {
      localStorage.setItem('tension_milestones', JSON.stringify(correctedAchievedMilestones));
      toast.info('Milestone count corrected to match actual daily check-ins.');
    }

    // Check for new achievements
    const newAchievements = potentialMilestones.filter(
      m => m.achieved && !correctedAchievedMilestones.some((am: any) => am.id === m.id)
    );

    if (newAchievements.length > 0) {
      const latest = newAchievements[0];
      latest.achievedDate = new Date().toISOString();
      
      // Add to achieved milestones
      const updated = [...correctedAchievedMilestones, ...newAchievements.map(m => ({
        ...m,
        achievedDate: new Date().toISOString(),
      }))];
      localStorage.setItem('tension_milestones', JSON.stringify(updated));
      
      // Show celebration
      setNewMilestone(latest);
      celebrateMilestone(latest);
      
      // analytics.track('milestone_achieved', {
      //   milestoneId: latest.id,
      //   type: latest.type,
      //   value: latest.value,
      // });
    }

    setMilestones(potentialMilestones);
  };

  const celebrateMilestone = (milestone: Milestone) => {
    // Trigger confetti
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    // Show notification
    if ('Notification' in window) {
      // DEFENSIVE: Wrap Notification API access in try-catch to prevent "Illegal constructor" errors
      try {
        if (Notification.permission === 'granted') {
          new Notification('🎉 Milestone Unlocked!', {
            body: milestone.title,
            icon: '/icons/icon-192x192.png',
            tag: `milestone-${milestone.id}`,
          });
        }
      } catch (error) {
        console.debug('Notification API error:', error);
      }
    }

    // Show toast
    toast.success(`🎉 Milestone Unlocked! ${milestone.title}`, {
      description: milestone.description,
      duration: 5000,
    });
  };

  const dismissCelebration = () => {
    setNewMilestone(null);
  };

  return (
    <>
      {/* Celebration Modal */}
      {newMilestone && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <motion.div
            initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            exit={{ scale: 0.5, opacity: 0 }}
            className="max-w-md w-full"
          >
            <Card className="p-8 text-center space-y-6 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30 border-amber-200 dark:border-amber-800">
              <div className="flex justify-center">
                <div className="p-4 rounded-full bg-amber-200 dark:bg-amber-800">
                  <newMilestone.icon className="w-12 h-12 text-amber-800 dark:text-amber-200" />
                </div>
              </div>
              
              <div>
                <h2 className="text-3xl font-light mb-2">🎉 Milestone Unlocked!</h2>
                <h3 className="text-2xl font-medium text-amber-900 dark:text-amber-100 mb-2">
                  {newMilestone.title}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-400">
                  {newMilestone.description}
                </p>
              </div>

              <div className="p-4 bg-white/50 dark:bg-neutral-900/50 rounded-lg">
                <p className="text-sm text-neutral-700 dark:text-neutral-300">
                  Keep going! Consistency builds awareness, and awareness builds better relationships.
                </p>
              </div>

              <Button onClick={dismissCelebration} className="w-full" size="lg">
                Continue
              </Button>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Milestones List */}
      <Card className="p-6">
        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5" />
          Milestones
        </h3>
        
        <div className="space-y-3">
          {milestones.map(milestone => {
            const Icon = milestone.icon;
            return (
              <div
                key={milestone.id}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  milestone.achieved
                    ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800'
                    : 'bg-neutral-50 dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 opacity-60'
                }`}
              >
                <div className={`p-2 rounded-full ${
                  milestone.achieved
                    ? 'bg-amber-200 dark:bg-amber-800'
                    : 'bg-neutral-200 dark:bg-neutral-800'
                }`}>
                  <Icon className={`w-5 h-5 ${
                    milestone.achieved
                      ? 'text-amber-800 dark:text-amber-200'
                      : 'text-neutral-500 dark:text-neutral-400'
                  }`} />
                </div>
                
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    {milestone.title}
                  </div>
                  <div className="text-xs text-neutral-600 dark:text-neutral-400">
                    {milestone.description}
                  </div>
                </div>
                
                {milestone.achieved && (
                  <div className="text-amber-600 dark:text-amber-400">
                    ✓
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {milestones.filter(m => m.achieved).length === 0 && (
          <p className="text-sm text-neutral-500 dark:text-neutral-500 text-center py-8">
            Keep checking in to unlock milestones!
          </p>
        )}
      </Card>
    </>
  );
}

// Helper functions
function parseArrayStorage(raw: string | null): any[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function extractDayKey(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString().slice(0, 10);
}

function uniqueDailyCheckInCount(rows: any[]): number {
  const dayKeys = new Set<string>();
  for (const row of rows) {
    const dayKey = extractDayKey(row?.date) || extractDayKey(row?.timestamp) || extractDayKey(row?.occurred_at);
    if (dayKey) dayKeys.add(dayKey);
  }
  return dayKeys.size;
}

async function resolveCanonicalCheckInCount(localCheckins: any[], accessToken?: string | null): Promise<number> {
  const localCount = uniqueDailyCheckInCount(localCheckins);
  if (!accessToken) return localCount;

  try {
    const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/checkins?limit=180`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${publicAnonKey}`,
        'X-User-Token': accessToken,
      },
    });
    if (!res.ok) return localCount;
    const payload = await res.json().catch(() => ({}));
    const serverCheckins = Array.isArray(payload?.checkIns) ? payload.checkIns : [];
    const serverCount = uniqueDailyCheckInCount(serverCheckins);
    return Math.max(localCount, serverCount);
  } catch {
    return localCount;
  }
}

function getConsecutiveDays(checkins: any[]): number {
  if (checkins.length === 0) return 0;

  const sortedDates = checkins
    .map(c => new Date(c.date).toDateString())
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

  let streak = 1;
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString();

  if (sortedDates[0] !== today && sortedDates[0] !== yesterday) {
    return 0; // Streak broken
  }

  for (let i = 1; i < sortedDates.length; i++) {
    const current = new Date(sortedDates[i - 1]);
    const previous = new Date(sortedDates[i]);
    const diffDays = Math.floor((current.getTime() - previous.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      streak++;
    } else {
      break;
    }
  }

  return streak;
}

function hasSignificantTensionDrop(checkins: any[]): boolean {
  if (checkins.length < 7) return false;

  const recent = checkins.slice(-7);
  const firstWeekAvg = recent.slice(0, 3).reduce((sum, c) => sum + c.rtiScore, 0) / 3;
  const lastWeekAvg = recent.slice(-3).reduce((sum, c) => sum + c.rtiScore, 0) / 3;

  return firstWeekAvg - lastWeekAvg >= 20;
}

function hasStableWeek(checkins: any[]): boolean {
  if (checkins.length < 7) return false;

  const recent = checkins.slice(-7);
  return recent.every(c => c.rtiScore <= 35);
}